import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  OrganizationUnitApiService,
} from '@core/api/services/organization-unit-api.service';

/**
 * DTO para una unidad organizacional
 */
export interface OrganizationUnitDto {
  id: string;
  name: string;
  organizationLevelId: number;
  parentUnitId?: string;
  // Propiedades alternativas para compatibilidad con código legacy
  levelId?: number;
  levelName?: string;
  ancestorNames?: string[];
}

/**
 * Servicio centralizado para operaciones con unidades organizacionales
 * Proporciona acceso a datos de estructura organizacional y comunidades
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationUnitService {
  constructor(private readonly apiService: OrganizationUnitApiService) {}

  /**
   * Obtiene todas las unidades organizacionales (sin paginación).
   */
  getAll(): Observable<OrganizationUnitDto[]> {
    return this.apiService.getAllWithoutPagination() as unknown as Observable<OrganizationUnitDto[]>;
  }

  /**
   * Obtiene una unidad organizacional por ID.
   */
  getById(id: string): Observable<OrganizationUnitDto> {
    return this.apiService.getById(id) as unknown as Observable<OrganizationUnitDto>;
  }

  /**
   * Busca unidades organizacionales con paginación, optimizado para autocomplete.
   */
  search(
    searchTerm: string = '',
    page: number = 1,
    pageSize: number = 20,
    levelId?: number
  ): Observable<{ items: OrganizationUnitDto[]; totalCount: number; totalPages: number }> {
    return (this.apiService.search(searchTerm, page, pageSize, levelId ?? null) as any).pipe(
      map((response: any) => ({
        items: (response.items ?? []) as OrganizationUnitDto[],
        totalCount: response.totalCount ?? 0,
        totalPages: response.totalPages ?? 0,
      }))
    );
  }

  /**
   * Obtiene todas las comunidades (unidades nivel 5).
   */
  getAllCommunities(): Observable<OrganizationUnitDto[]> {
    return this.getAll().pipe(
      map((units: OrganizationUnitDto[]) =>
        units.filter(u => u.organizationLevelId === 5)
      )
    );
  }

  /**
   * Obtiene una comunidad por ID.
   */
  getCommunityById(id: string): Observable<OrganizationUnitDto> {
    return this.getById(id).pipe(
      map(u => {
        if (u.organizationLevelId !== 5) {
          throw new Error(`Unit ${id} is not a community (level 5)`);
        }
        return u;
      })
    );
  }
}
